import { Link } from 'react-router-dom';
import type React from 'react';

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  meta,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  meta?: React.ReactNode;
}) {
  return (
    <div className="ui-page-header">
      <div className="ui-page-header-copy">
        {eyebrow && <div className="ui-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        {meta && <div className="ui-page-meta">{meta}</div>}
      </div>
      {actions && <div className="ui-page-actions">{actions}</div>}
    </div>
  );
}

export function MetricCard({
  label,
  value,
  context,
  tone = 'neutral',
}: {
  label: string;
  value: React.ReactNode;
  context?: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'ai';
}) {
  return (
    <div className={`ui-metric-card tone-${tone}`}>
      <div className="ui-metric-label">{label}</div>
      <div className="ui-metric-value">{value}</div>
      {context && <div className="ui-metric-context">{context}</div>}
    </div>
  );
}

export function StatusBadge({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'ai';
}) {
  return <span className={`ui-status-badge tone-${tone}`}>{children}</span>;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="ui-empty-state">
      <div className="ui-empty-mark" aria-hidden="true" />
      <h2>{title}</h2>
      {description && <p>{description}</p>}
      {action && <div className="ui-empty-action">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'We could not load this view.',
  message,
  onRetry,
}: {
  title?: string;
  message?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div className="ui-error-state" role="alert">
      <div>
        <strong>{title}</strong>
        {message && <span>{message}</span>}
      </div>
      {onRetry && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function LoadingSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="ui-skeleton-stack" aria-label="Loading">
      {Array.from({ length: rows }).map((_, index) => (
        <div className="ui-skeleton" key={index} />
      ))}
    </div>
  );
}

export function DataTable({
  children,
  labelledBy,
}: {
  children: React.ReactNode;
  labelledBy?: string;
}) {
  return (
    <div className="ui-table-wrap">
      <table className="ui-data-table" aria-labelledby={labelledBy}>
        {children}
      </table>
    </div>
  );
}

export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  if (!open) return null;

  return (
    <div className="ui-drawer-backdrop" onClick={onClose}>
      <aside className="ui-drawer" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header className="ui-drawer-header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" className="ui-icon-btn" aria-label="Close" onClick={onClose}>
            x
          </button>
        </header>
        <div className="ui-drawer-body">{children}</div>
        {footer && <footer className="ui-drawer-footer">{footer}</footer>}
      </aside>
    </div>
  );
}

export function ActionLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link className="btn btn-secondary btn-sm" to={to}>
      {children}
    </Link>
  );
}
